import { VariantenWechselDetails } from "./MultiInstanceRecoLoader";
import { PersonalisierungConsent, RichRelevanceConsent } from "./RecoConsent";

export type RecoQueryParameters = {
  syan: string | null;
  farbId: string | null;
  categoryId: string | null;
  produktGruppe: string | null;
  markenName: string | null;
  searchterm: string | null;
  orderId: string | null;
  cartpos: string[];
  inCart: string[];
  useMock: string | null;
};
export type RecommendationResponse = Record<string, string>;
export type FetchFn = typeof fetch;

type BuildRichRelevanceUrl = (
  placements: string[],
  countryUrlPrefix: string,
  queryParams: RecoQueryParameters,
  origin: string,
  richRelevanceConsent: RichRelevanceConsent,
  personalisierungConsent: PersonalisierungConsent,
  eventDetails: VariantenWechselDetails | null,
) => URL;

const buildRichRelevanceUrl: BuildRichRelevanceUrl = (
  placements,
  countryUrlPrefix,
  queryParams,
  origin,
  richRelevanceConsent,
  personalisierungConsent,
  eventDetails,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const path: string =
    countryUrlPrefix + "/entd/frontend-recommendations/render/fetchAll";

  const urlSearchParams = new URLSearchParams();

  if (eventDetails) {
    urlSearchParams.append("produktId", eventDetails.syan);
    if (eventDetails.farbId !== undefined) {
      urlSearchParams.append("farbId", eventDetails.farbId);
    }
  } else {
    if (queryParams.syan !== null) {
      urlSearchParams.append("produktId", queryParams.syan);
    }
    if (queryParams.farbId !== null) {
      urlSearchParams.append("farbId", queryParams.farbId);
    }
  }

  if (queryParams.categoryId !== null) {
    urlSearchParams.append("categoryId", queryParams.categoryId);
  }
  if (queryParams.produktGruppe !== null) {
    urlSearchParams.append("produktGruppe", queryParams.produktGruppe);
  }
  if (queryParams.markenName !== null) {
    urlSearchParams.append("markenName", queryParams.markenName);
  }
  if (queryParams.searchterm !== null) {
    urlSearchParams.append("searchterm", queryParams.searchterm);
  }
  if (queryParams.orderId !== null) {
    urlSearchParams.append("orderId", queryParams.orderId);
  }
  if (queryParams.useMock) {
    urlSearchParams.append("useMock", queryParams.useMock);
  }

  queryParams.cartpos.forEach((pos) => urlSearchParams.append("cartpos", pos));

  queryParams.inCart.forEach((id) => urlSearchParams.append("incart", id));

  placements.forEach((placement) => {
    if (placement) {
      urlSearchParams.append("placement", placement);
    }
  });

  urlSearchParams.append("entd_consent", `${richRelevanceConsent}`);
  urlSearchParams.append("perso_consent", `${personalisierungConsent}`);

  const url = new URL(path, origin);
  const searchString = urlSearchParams.toString();
  if (searchString) {
    url.search = searchString;
  }
  return url;
};

type CallRecommendationsApi = (
  fetchFn: FetchFn,
  url: URL,
) => Promise<RecommendationResponse>;

const callRecommendationsApi: CallRecommendationsApi = async (
  fetchFn: FetchFn,
  url: URL,
) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), 3000);
  const response = await fetchFn(url.toString(), {
    method: "GET",
    signal: controller.signal,
  });
  clearTimeout(id);
  return (await response.json()) as RecommendationResponse;
};

export type InjectedFetchRecommendations = (
  placements: string[],
  countryUrlPrefix: string,
  queryParameters: RecoQueryParameters,
  origin: string,
  richRelevanceConsent: RichRelevanceConsent,
  personalisierungConsent: PersonalisierungConsent,
  eventDetails: VariantenWechselDetails | null,
) => Promise<RecommendationResponse>;

export type FetchRecommendations = (
  fetchFn: FetchFn,
) => InjectedFetchRecommendations;

export const fetchRecommendations: FetchRecommendations =
  (fetchFn) =>
  (
    placements,
    countryUrlPrefix,
    queryParameters,
    origin,
    richRelevanceConsent,
    personalisierungConsent,
    eventDetails,
  ) => {
    const url = buildRichRelevanceUrl(
      placements,
      countryUrlPrefix,
      queryParameters,
      origin,
      richRelevanceConsent,
      personalisierungConsent,
      eventDetails,
    );
    return callRecommendationsApi(fetchFn, url);
  };
